<script setup>
import { ref, onMounted, computed } from 'vue'; // Import 'computed'
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const plants = ref([]);
const newPlant = ref({ PlantNo: '', PlantName: '' });
const editingPlant = ref(null); // Holds the plant being edited

// Added selectedPlant ref to track the row clicked in the table for Edit/Delete
const selectedPlant = ref(null);


// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
const apiUrl = '${API_BASE_URL}/api/plants';

const loading = ref(false); // Add loading state
const error = ref(null); // Add error state
const notification = ref({ // Add notification state
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model ---
// This computed property handles the Plant Number input
const currentPlantNo = computed({
  get() {
    // If editing, get the PlantNo from editingPlant, otherwise from newPlant
    return editingPlant.value ? editingPlant.value.PlantNo : newPlant.value.PlantNo;
  },
  set(value) {
    // If editing, update the PlantNo in editingPlant, otherwise in newPlant
    if (editingPlant.value) {
      editingPlant.value.PlantNo = value;
    } else {
      newPlant.value.PlantNo = value;
    }
  }
});

// This computed property handles the Plant Name input
const currentPlantName = computed({
  get() {
    // If editing, get the PlantName from editingPlant, otherwise from newPlant
    return editingPlant.value ? editingPlant.value.PlantName : newPlant.value.PlantName;
  },
  set(value) {
    if (editingPlant.value) {
      editingPlant.value.PlantName = value;
    } else {
      newPlant.value.PlantName = value;
    }
  }
});
// --- End Computed Properties ---


// Fetch all plants
const fetchPlants = async () => {
  loading.value = true;
  error.value = null;
  selectedPlant.value = null; // Deselect any selected plant when refreshing
  try {
    const response = await axios.get(apiUrl);
    plants.value = response.data;
  } catch (err) { // Use err for consistency
    console.error('Error fetching plants:', err);
    error.value = 'Failed to fetch plants. Please try again later.';
    showNotification(error.value, 'error'); // Use notification system
  } finally {
    loading.value = false;
  }
};

// Add a new plant
const createPlant = async () => {
  // Use newPlant.value directly as the form is bound to it when not editing
  if (!newPlant.value.PlantNo.trim()) {
    showNotification('Plant Number is required.', 'warning'); // Use notification
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const response = await axios.post(apiUrl, newPlant.value);
    plants.value.push(response.data);
    newPlant.value = { PlantNo: '', PlantName: '' }; // Clear form
    showNotification('Plant created successfully!', 'success'); // Success notification
  } catch (err) { // Use err
    console.error('Error creating plant:', err);
    if (err.response?.status === 409) {
      error.value = 'Plant Number already exists.';
    } else {
      error.value = 'Failed to create plant.';
    }
    showNotification(error.value, 'error'); // Error notification
  } finally {
    loading.value = false;
  }
};

// Select a plant from the table
const selectPlant = (plant) => {
  selectedPlant.value = plant;
   // If you select a plant while editing, stop editing
    if (editingPlant.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors
}

// Start editing
const startEditing = () => {
  if (selectedPlant.value) {
      editingPlant.value = { ...selectedPlant.value };
      // When starting edit, clear newPlant form and any errors
      newPlant.value = { PlantNo: '', PlantName: '' };
      error.value = null;
  } else {
       showNotification('Please select a plant to edit.', 'warning');
  }
};

// Cancel editing
const cancelEditing = () => {
  editingPlant.value = null;
  // When canceling edit, clear newPlant form and any errors
  newPlant.value = { PlantNo: '', PlantName: '' };
  error.value = null;
};

// Update plant
const updatePlant = async () => {
  // Use editingPlant.value directly as the form is bound to it when editing
  if (!editingPlant.value.PlantName.trim()) {
    showNotification('Plant Name is required.', 'warning'); // Use notification
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    // Send the entire editingPlant object, backend should only use updateable fields
    const response = await axios.put(`${apiUrl}/${editingPlant.value.PlantNo}`, editingPlant.value);

    const index = plants.value.findIndex(p => p.PlantNo === editingPlant.value.PlantNo);
    if (index !== -1) {
      // Update the plant in the list with the data from the response
      // Ensure all original properties are kept if backend doesn't return them
      plants.value[index] = { ...plants.value[index], ...response.data };
    }
     // If the updated plant was selected, keep it selected with updated data
     if (selectedPlant.value && selectedPlant.value.PlantNo === editingPlant.value.PlantNo) {
         selectedPlant.value = { ...selectedPlant.value, ...response.data };
     }

    editingPlant.value = null; // Exit editing mode
    showNotification('Plant updated successfully!', 'success'); // Success notification
  } catch (err) { // Use err
    console.error('Error updating plant:', err);
    if (err.response?.status === 404) {
      error.value = 'Plant not found.';
    } else {
      error.value = 'Failed to update plant.';
    }
    showNotification(error.value, 'error'); // Error notification
  } finally {
    loading.value = false;
  }
};

// Delete plant
const deletePlant = async () => {
   if (!selectedPlant.value) {
       showNotification('Please select a plant to delete.', 'warning');
       return;
  }
  const plantNoToDelete = selectedPlant.value.PlantNo;

  if (confirm(`Are you sure you want to delete plant ${plantNoToDelete}? This will also delete all associated sites.`)) {
    loading.value = true;
    error.value = null;
    try {
      // DELETE request uses PlantNo in the URL
      await axios.delete(`${apiUrl}/${plantNoToDelete}`);
      plants.value = plants.value.filter(p => p.PlantNo !== plantNoToDelete);
      // If the deleted plant was selected or being edited, clear states
      selectedPlant.value = null;
      if(editingPlant.value && editingPlant.value.PlantNo === plantNoToDelete) {
          editingPlant.value = null;
      }
      showNotification('Plant deleted successfully!', 'success'); // Success notification
    } catch (err) { // Use err
      console.error('Error deleting plant:', err);
      if (err.response?.status === 404) {
        error.value = 'Plant not found.';
      } else if (err.response?.status === 409) {
         // Specific error message from the backend about FK conflicts elsewhere
         error.value = err.response.data || 'Conflict: Cannot delete plant because it is referenced by other entities (e.g., sites).';
      }
      else {
        error.value = 'Failed to delete plant.';
      }
      showNotification(error.value, 'error'); // Error notification
    } finally {
      loading.value = false;
    }
  }
};

// Show notification helper function
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000); // Hide after 5 seconds
};


// Fetch data on mount
onMounted(fetchPlants);
</script>

<template>
  <div class="sites-page">
    <div class="container">
      <div class="form-panel">
        <h2>{{ editingPlant ? 'Edit Plant' : 'Add New Plant' }}</h2>
        <form @submit.prevent="editingPlant ? updatePlant() : createPlant()">
          <div class="form-group">
            <label for="plant_no">Plant Number:</label>
             <input type="text" id="plant_no" v-model="currentPlantNo" required
                   :disabled="editingPlant !== null || loading"> </div>
          <div class="form-group">
            <label for="plant_name">Plant Name:</label>
             <input type="text" id="plant_name" v-model="currentPlantName"
                   :disabled="loading"> </div>
          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingPlant ? 'Update Plant' : 'Save Plant' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingPlant" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Plant List</h2>

         <div v-if="loading && plants.length === 0">Loading plants...</div>
         <div v-if="error && plants.length === 0" class="error-message">{{ error }}</div>

        <div class="table-actions">
           <button @click="addNew" :disabled="loading">Add New</button>
           <button @click="startEditing" :disabled="!selectedPlant || loading">Edit</button>
           <button @click="deletePlant" :disabled="!selectedPlant || loading">Delete</button>
           <button @click="fetchPlants" :disabled="loading">Refresh</button>
         </div>

        <table class="site-table">
          <thead>
            <tr>
              <th>Plant No</th>
              <th>Plant Name</th>
              </tr>
          </thead>
          <tbody>
            <tr v-for="plant in plants" :key="plant.PlantNo"
                :class="{ 'selected': selectedPlant && selectedPlant.PlantNo === plant.PlantNo }"
                @click="selectPlant(plant)"> <td>{{ plant.PlantNo }}</td>
              <td>{{ plant.PlantName }}</td>
              </tr>
             <tr v-if="plants.length === 0 && !loading">
                <td colspan="2" style="text-align: center;">No plants found.</td>
             </tr>
          </tbody>
        </table>
      </div>
    </div>

     <div v-if="notification.show" :class="['notification', notification.type]">
      {{ notification.message }}.
    </div>

  </div>
</template>

<style scoped>
/* Copying the exact styles from your SitesView */
.sites-page {
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f4f6f8;
}

.container {
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap; /* Allow wrapping on small screens */
}

.form-panel {
  flex: 1; /* Form panel takes 1 part of the available space */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
}

.table-panel {
  flex: 2; /* Table panel takes 2 parts of the available space (making it wider) */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
  overflow-x: auto; /* Add horizontal scroll if table content is too wide */
}


h2 {
  color: #333;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  color: #555;
  font-weight: bold;
}

input[type="text"],
input[type="number"],
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
  border-color: #007bff;
  outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-actions {
  margin-top: 20px;
}

.save-button,
.cancel-button {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.save-button {
  background-color: #28a745; /* Green */
  color: white;
  margin-right: 10px;
}

.save-button:hover:not(:disabled) {
  background-color: #218838;
}

.cancel-button {
  background-color: #6c757d; /* Gray */
  color: white;
}

.cancel-button:hover:not(:disabled) {
  background-color: #5a6268;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}


.table-actions {
  margin-bottom: 15px;
  display: flex;
  gap: 10px; /* Space between buttons */
  flex-wrap: wrap;
}

.table-actions button {
  padding: 8px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background-color: #007bff; /* Blue */
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease, opacity 0.3s ease;
}

/* Specific button styles from SitesView */
.table-actions button:nth-child(2) { /* Edit button */
  background-color: #ffc107; /* Yellow */
  border-color: #ffc107;
  color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
  background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
  background-color: #dc3545; /* Red */
  border-color: #dc3545;
  color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
  background-color: #c82333;
}


.table-actions button:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}


.site-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.9rem;
}

/* Increased padding for bigger cells */
.site-table th,
.site-table td {
  padding: 12px; /* Increased from 10px */
  border: 1px solid #ddd;
  text-align: left;
}

.site-table th {
  background-color: #e9ecef; /* Light gray */
  font-weight: bold;
  color: #495057;
}

.site-table tbody tr:hover {
  background-color: #f8f9fa; /* Lighter gray on hover */
  cursor: pointer;
}

.site-table tbody tr.selected {
  background-color: #cfe2ff; /* Light blue when selected */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  .form-panel,
  .table-panel {
    margin-bottom: 20px;
    min-width: unset; /* Remove min-width on small screens */
    flex: unset; /* Remove flex on small screens */
    width: 100%; /* Make panels full width */
  }

  .table-actions {
    flex-direction: column; /* Stack buttons vertically */
    gap: 5px;
  }

  .table-actions button {
    width: 100%; /* Make buttons full width */
    margin-right: 0;
  }
}

/* Notification Styling */
.notification {
  position: fixed;
  bottom: 20px;
  left: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  color: white;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: opacity 0.5s ease-in-out;
}

.notification.success {
  background-color: #28a745; /* Green */
}

.notification.error {
  background-color: #dc3545; /* Red */
}

.notification.warning {
  background-color: #ffc107; /* Yellow */
  color: #212529;
}

.error-message {
  color: #dc3545;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #dc3545;
  background-color: #fff3f3;
  border-radius: 4px;
}

</style>
